<script lang="ts" setup>
import { storeToRefs } from "pinia";
import BreadcrumbComponent from "~/core/components/BreadcrumbComponent.vue";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import {
  GROUP_CODE_NEW,
  PATH_USER_MANAGEMENT_GROUPS,
} from "~/modules/user/constants";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import userGroupService from "~/modules/user/services/userGroup.service";
import { useUserSession } from "~/modules/user/stores/useUserSession";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { userSession } = useUserSession();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const userGroupCode = route?.params?.code as string;
const isCreate = userGroupCode === GROUP_CODE_NEW;
const isLoading = ref(true);
const error = ref<LxcError | null>(null);
const organizationCode = currentOrganization.value?.code;
const breadcrumb: Ref<InstanceType<typeof BreadcrumbComponent> | null> =
  ref(null);

const userGroupForm = ref();
const userGroup = reactive({
  label: "",
  orgCode: userSession?.entity?.code,
  sectors: [] as number[] | undefined,
});
const userGroupLabel = ref("");
const userGroupFormValidationRules = {
  label: [
    { required: true, message: t("input.error.required"), trigger: "blur" },
    { max: 50, message: t("input.error.maxLength", { maxLength: 50 }) },
  ],
};

const name = computed(() => userGroupLabel.value || "userGroup.button.create");

onMounted(async () => await getUserGroup());

/**
 * Update or create userGroup resource
 */
async function onSaveUserGroup(): Promise<void> {
  const valid = await userGroupForm.value.validate().catch(() => false);

  if (valid) {
    if (organizationCode) {
      const userGroupDto: GroupsUserV2 = {
        name: userGroup.label,
        sectorCodes: userGroup.sectors,
      };

      const response = isCreate
        ? await userGroupService.createOrganizationUserGroup(
            organizationCode,
            userGroupDto,
          )
        : await userGroupService.updateOrganizationUserGroup(
            organizationCode,
            userGroupLabel.value,
            userGroupDto,
          );

      if (LxcError.check(response)) {
        response.notify(NotificationKey.saveError);
      } else {
        showNotificationSuccess(t(NotificationKey.saveSuccess));
        await breadcrumb.value?.goBack();
      }
    } else {
      throw new Error("Organization code is not defined");
    }
  }
}

/**
 * Retrieve UserGroup if updating
 */
async function getUserGroup() {
  if (!isCreate) {
    const userGroupResponse =
      await userGroupService.getOrganizationUserGroupByCode(
        organizationCode,
        userGroupCode,
      );

    if (LxcError.check(userGroupResponse)) {
      error.value = userGroupResponse;
    } else {
      userGroup.label = userGroupResponse.name;
      userGroup.sectors = userGroupResponse.sectorCodes;
      userGroupLabel.value = userGroupResponse.name;
    }
  }

  isLoading.value = false;
}
</script>

<template>
  <el-container data-cy="page-userGroup-component" direction="vertical">
    <breadcrumb-component
      ref="breadcrumb"
      :name="isLoading ? '' : name"
      class="px-8 mt-8"
      :should-not-translate="!isCreate"
      :back-default-route="PATH_USER_MANAGEMENT_GROUPS"
    />
    <container-component
      class="half-container"
      :error="error"
      :is-loading="isLoading"
    >
      <mandatory-component />
      <el-form
        ref="userGroupForm"
        :rules="userGroupFormValidationRules"
        :model="userGroup"
        label-position="left"
        label-width="200px"
        @submit.prevent="onSaveUserGroup"
      >
        <el-form-item :label="t('input.label')" prop="label">
          <el-input
            v-model="userGroup.label"
            data-cy="inputLabel"
            type="text"
          />
        </el-form-item>
      </el-form>

      <cancel-or-submit-buttons
        @cancel="router.back()"
        @submit="onSaveUserGroup"
      />
    </container-component>
  </el-container>
</template>
